// src/private/src/pages/app/clients/components/SavedViews.jsx
import React, { memo, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import DeleteIcon from '@mui/icons-material/Delete';

const STORAGE_KEY = 'dashboardSavedViews';

function loadSavedViews() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {
    // ignore
  }
  return [];
}

function persistViews(views) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(views));
  } catch {
    // ignore
  }
}

/**
 * Saved views component — lets users save and load named presets
 * of column selection, date range, and filters.
 */
function SavedViews({ visibleColumns, onApplyView }) {
  const [views, setViews] = useState(() => loadSavedViews());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewName, setViewName] = useState('');

  const handleSave = useCallback(() => {
    if (!viewName.trim()) return;
    const newView = {
      name: viewName.trim(),
      columns: [...visibleColumns],
      savedAt: new Date().toISOString(),
    };
    const updated = [...views.filter((v) => v.name !== newView.name), newView];
    setViews(updated);
    persistViews(updated);
    setDialogOpen(false);
    setViewName('');
  }, [viewName, visibleColumns, views]);

  const handleLoad = useCallback(
    (view) => {
      if (view.columns && onApplyView) {
        onApplyView(new Set(view.columns));
      }
    },
    [onApplyView]
  );

  const handleDelete = useCallback(
    (name) => {
      const updated = views.filter((v) => v.name !== name);
      setViews(updated);
      persistViews(updated);
    },
    [views]
  );

  return (
    <>
      <Stack direction="row" spacing={0.5} alignItems="center">
        {views.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {views.map((view) => (
              <Chip
                key={view.name}
                icon={<BookmarkIcon sx={{ fontSize: '0.85rem' }} />}
                label={view.name}
                size="small"
                variant="outlined"
                onClick={() => handleLoad(view)}
                onDelete={() => handleDelete(view.name)}
                deleteIcon={<DeleteIcon sx={{ fontSize: '0.75rem' }} />}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  cursor: 'pointer',
                }}
              />
            ))}
          </Stack>
        )}
        <Button
          size="small"
          variant="text"
          startIcon={<BookmarkAddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.7rem',
            minWidth: 'auto',
            whiteSpace: 'nowrap',
          }}
        >
          Save View
        </Button>
      </Stack>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Save Current View</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Saves your current column selection. You can load it later with one click.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="View name"
            value={viewName}
            onChange={(e) => setViewName(e.target.value)}
            placeholder="e.g., Weekly Ops, Revenue Review"
            size="small"
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!viewName.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default memo(SavedViews);
